import React from "react"
import Layout from "../../components/layout"
import Image from "../../components/image"
import { AppendixWaypoint, Appendix } from "../../components/appendix"
import Hero from "../../components/hero"
import { PageColumns, Column } from "../../components/page-column"
import MobileOnly from "../../components/mobile-only"
import Table from "../../components/table"
import AssesmentsCompletedData from "../../data/assessments-completed.json"
import ReadNext from "../../components/read-next"

const PAGESLUG = "accountability"

export default function Accountability() {
  return (
    <Layout>
      <section className="page page--pullBottom text-block page--margin-medium">
        <Hero
          title="Accountability"
          text="The IMF, a global organization governed by and accountable to its 190 member countries, has a system of checks and balances to ensure accountability—ranging from internal and external audits to risk management and evaluation of its policies and operations. Similarly, the IMF staff is expected to observe the highest ethical and workplace standards of conduct."
          image="who-we-are/accountability/hero.jpg"
        />

        <PageColumns>
          <Column type="media">
            <Image path="who-we-are/accountability/accountability_1.jpg" />
          </Column>

          <Column type="text">
            <h2>Checks and balances</h2>
            <p>
              The IMF conducts audits of all its operations. The audit
              mechanisms are set up to improve governance, transparency, and
              accountability and include an external audit firm, an independent
              External Audit Committee, and the Office of Internal Audit. The
              External Audit Committee is independent of the IMF’s management
              and oversees the annual audit.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={1} />
            <p>
              The Office of Internal Audit provides an independent assurance and
              advisory function designed to protect and strengthen the IMF. It
              has two key mandates: to assess and improve the effectiveness of
              the IMF’s governance, risk management processes, and internal
              controls and to catalyze improvement of the IMF’s business
              processes by advising on best practices and the development of
              cost-effective control solutions.
            </p>
            <MobileOnly>
              <Image path="who-we-are/accountability/accountability_1.jpg" />
            </MobileOnly>
          </Column>
        </PageColumns>

        {/* Managing Enterprise Risk */}
        <PageColumns>
          <Column type="text">
            <h2>Managing enterprise risk</h2>
            <p>
              The IMF’s internal Office of Risk Management, established in 2014,
              is responsible for developing and facilitating an integrated
              process for managing enterprise-wide risks—including core business
              as well as strategic, financial, operational, and reputational
              risks—while enabling risk-intelligent capabilities for the IMF to
              fulfill its mandate.
            </p>
          </Column>

          <Column type="media"></Column>
        </PageColumns>

        {/* Learning From Experience */}
        <PageColumns modifiers="md:pull--large">
          <Column type="media"></Column>

          <Column type="text">
            <h2>Learning from experience</h2>
            <p>
              The Independent Evaluation Office (IEO) was established in 2001 to
              conduct independent and objective evaluations of IMF policies and
              activities. The IEO is fully independent of IMF management and
              staff and operates at arm’s length from the Executive Board. Its
              mission is to promote learning within the IMF, strengthen the
              institution’s external credibility, and support institutional
              governance and oversight. Recently completed IEO evaluations have
              focused on the IMF’s work with partners, advice on capital flows,
              unconventional monetary policies, and financial surveillance. More
              information about the IEO, including its terms of reference and
              evaluation reports, is available at{" "}
              <a href="https://ieo.imf.org">https://ieo.imf.org</a>.
            </p>
            <MobileOnly>
              <Image path="who-we-are/accountability/accountability_2.jpg" />
            </MobileOnly>
          </Column>
        </PageColumns>

        {/* Ethics and Staff Conduct */}
        <PageColumns modifiers="md:pull--small">
          <Column type="text">
            <h2>Ethics and staff conduct</h2>
            <p>
              To ensure good governance within the organization, the IMF has
              adopted integrity measures, including a code of conduct for staff
              that includes financial certification and disclosure requirements
              and sanctions. A similar code of conduct is in place for members
              of the Executive Board. An integrity hotline offers protection to
              whistleblowers.
            </p>
            <p>
              The IMF Ethics Office advises the institution and its staff on
              ethical behavior, investigates alleged violations of rules and
              regulations, and oversees the ethics and integrity training
              program for all staff members. An independent ombudsperson is also
              available to provide impartial and independent assistance in
              resolving employment-related problems.
            </p>
          </Column>

          <Column type="media">
            <Image path="who-we-are/accountability/accountability_2.jpg" />
          </Column>
        </PageColumns>

        {/* Engagement With the Public */}
        <PageColumns modifiers="md:pull--xs">
          <Column type="media"></Column>

          <Column type="text">
            <h2>Engagement with the public</h2>
            <p>
              The IMF meets regularly with political leaders and country
              authorities and routinely engages with a wide range of private
              sector representatives, the media, and nongovernment stakeholders
              such as the academic community, civil society organizations,
              parliamentarians, labor unions, and youth leaders. Opportunities
              for such two-way dialogue allow the IMF both to explain its
              approaches and to learn from others to improve its policy advice.
            </p>
          </Column>
        </PageColumns>

        {/* Safeguards Assessments  */}
        <PageColumns modifiers="md:pull--large">
          <Column type="text">
            <h2>Safeguards Assessments</h2>
            <AppendixWaypoint page={PAGESLUG} id={2} />
            <p>
              When the IMF provides financing to a member country, a Safeguards
              Assessment is carried out to provide reasonable assurance that the
              country’s central bank can manage the IMF resources and provide
              reliable monetary data on the IMF-supported program.
            </p>
            <p>
              The assessments involve an evaluation of central bank operations
              in five areas: (1) the external audit mechanism, (2) the legal
              structure and autonomy, (3) the financial reporting framework, (4)
              the internal audit mechanism, and (5) the system of internal
              controls. At the end of April 2021, 346 assessments had been
              conducted, covering 99 central banks; 24 of these assessments were
              completed in FY 2021.
            </p>
            <div style={{ marginBottom: 20, marginTop: 20 }}>
              <h4 style={{ marginBottom: 20 }}>
                The assessments involve an evaluation of central bank operations
                in five areas:
              </h4>
              <Image path="who-we-are/accountability/accountability_3.jpg" />
            </div>
            <p>
              The IMF monitors progress as central banks work to improve their
              safeguards frameworks and address IMF recommendations. The
              monitoring continues for as long as IMF credit remains
              outstanding. About 85 central banks are currently subject to
              monitoring, with an increase of 22 central banks in FY 2021 owing
              to the elevated activity resulting from the financing extended to
              member countries to address the impact of the COVID-19 pandemic.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={3} />

            <MobileOnly>
              <Table
                data={AssesmentsCompletedData}
                title="FY2021 Assessments Completed"
                subTitle="As of April 30, 2021"
                classes="table--two-col"
              />
            </MobileOnly>
            <p>
              The IMF also conducts fiscal safeguards reviews of state
              treasuries when a member requests exceptional access to IMF
              resources for which a substantial portion of the funds—at least 25
              percent—is directed toward financing the state budget. During FY
              2021, two fiscal safeguards reviews were conducted.
            </p>

            <p className="text-block__footnotes">
              <a href="https://www.imf.org/external/np/tre/safegrds/complete/index.aspx">
                View all Safeguards Assessments completed to date.
              </a>
            </p>
          </Column>

          <Column type="media" classes="page-columns__column--align-end">
            <Table
              data={AssesmentsCompletedData}
              title="FY2021 Assessments Completed"
              subTitle="As of April 30, 2021"
              classes="table--two-col"
            />
          </Column>
        </PageColumns>
      </section>
      <Appendix page={PAGESLUG} />
      <ReadNext
        sectionID="who-we-are"
        pageID="corporate-social-responsibility"
      />
    </Layout>
  )
}
